/**
 * Pre-canned sample cases shipped with the SPA so users can take a "test
 * drive" of every module without uploading anything. Each sample describes:
 *   - id            : URL-safe identifier referenced by ?sample=<id>
 *   - moduleId      : workflowModules.id this sample targets
 *   - title / desc  : shown on Test Drive cards + inside the picker
 *   - payload       : module-specific input the page should auto-load + run
 *
 * Pages opt in by reading the `sample` query string and, if found, calling
 * a small `applySample(payload)` function before triggering Run.
 */

export const TEST_CASES = [
  // -----------------------------------------------------------------------
  // Denial Resolve -- inline 835 payload (no upload, runs scrubber+routing)
  // -----------------------------------------------------------------------
  {
    id: 'denial-co151-units',
    moduleId: 'denial-resolve',
    title: 'CO-151: Units exceed documented support',
    desc: 'A 99214 billed with 2 units denied for medical necessity (units beyond what was documented).',
    payload: {
      kind: 'inline-claim',
      patient: 'R. Alvarez',
      payer: 'BCBS',
      claimReference: 'DEMO-DENIAL-001',
      payload: {
        claimInformation: {
          serviceLines: [
            { professionalService: { procedureCode: '99214', serviceUnitCount: 2, procedureModifiers: ['25'] } },
          ],
          healthCareCodeInformation: [{ diagnosisCode: 'M54.5' }],
        },
      },
      remittance: {
        carcCodes: ['CO-151'],
        rarcCodes: ['M62'],
        deniedAmount: 220,
        adjustments: [{ groupCode: 'CO', reasonCode: '151', amount: 220, description: 'Payment adjusted because the payer deems the information submitted does not support this many/frequency of services.' }],
      },
    },
  },
  {
    id: 'denial-co27-coverage-terminated',
    moduleId: 'denial-resolve',
    title: 'CO-27: Coverage terminated -- wrong payer',
    desc: 'Eligibility denial -- not an appeal. Re-route to the correct active coverage.',
    payload: {
      kind: 'inline-claim',
      patient: 'M. Chen',
      payer: 'BCBS PPO (terminated)',
      claimReference: 'DEMO-DENIAL-002',
      payload: {
        claimInformation: {
          serviceLines: [
            { professionalService: { procedureCode: '99213', serviceUnitCount: 1 } },
          ],
          healthCareCodeInformation: [{ diagnosisCode: 'E11.9' }],
        },
      },
      remittance: {
        carcCodes: ['CO-27'],
        rarcCodes: [],
        deniedAmount: 145,
        adjustments: [{ groupCode: 'CO', reasonCode: '27', amount: 145, description: 'Expenses incurred after coverage terminated.' }],
      },
    },
  },

  // -----------------------------------------------------------------------
  // Claim Right -- pre-fills the 837 JSON payload
  // -----------------------------------------------------------------------
  {
    id: 'cr-modifier-25-missing',
    moduleId: 'claim-right',
    title: 'E/M + procedure same day: 25 modifier missing',
    desc: 'A 99214 billed same day as 11042 -- the office visit needs modifier 25 or the payer will bundle it.',
    payload: {
      payload: {
        claimInformation: {
          serviceLines: [
            { professionalService: { procedureCode: '99214', serviceUnitCount: 1 } },
            { professionalService: { procedureCode: '11042', serviceUnitCount: 1 } },
          ],
          healthCareCodeInformation: [{ diagnosisCode: 'L97.521' }],
        },
      },
    },
  },
  {
    id: 'cr-mue-cap-breached',
    moduleId: 'claim-right',
    title: 'MUE cap breached (J0696 units)',
    desc: 'A J-code billed with units above the Medicare Practitioner MUE cap.',
    payload: {
      payload: {
        claimInformation: {
          serviceLines: [
            { professionalService: { procedureCode: 'J0696', serviceUnitCount: 250 } },
          ],
          healthCareCodeInformation: [{ diagnosisCode: 'J20.9' }],
        },
      },
    },
  },

  // -----------------------------------------------------------------------
  // Medical Necessity
  // -----------------------------------------------------------------------
  {
    id: 'mn-mri-lumbar',
    moduleId: 'medical-necessity',
    title: 'Lumbar MRI (72148) for M54.5',
    desc: 'Check Medicare LCD/NCD support for a lumbar MRI with low back pain.',
    payload: { codes: '72148', icd: 'M54.5', clinicalText: 'Patient: J. Miller, 58 y/o male.\nCPT/HCPCS: 72148 — MRI lumbar spine without contrast.\nICD-10: M54.5 — Low back pain.\nPayer: Medicare Part B.\nClinical note: Patient presents with chronic low back pain radiating to left lower extremity for 8 weeks, unresponsive to 6 weeks of conservative therapy (physical therapy and NSAIDs). New neurological deficit noted on exam: diminished left Achilles reflex and L5 dermatomal sensory loss. MRI ordered to evaluate for disc herniation or spinal stenosis. No prior advanced imaging in past 12 months.' },
  },
  {
    id: 'mn-debridement',
    moduleId: 'medical-necessity',
    title: 'Wound debridement (11042) for L97.521',
    desc: 'Subcutaneous debridement for a non-pressure chronic ulcer of left foot.',
    payload: { codes: '11042', icd: 'L97.521', clinicalText: 'Patient: R. Thompson, 72 y/o female with diabetes.\nCPT/HCPCS: 11042 — Debridement, subcutaneous tissue.\nICD-10: L97.521 — Non-pressure chronic ulcer of other part of left foot with fat layer exposed.\nPayer: Medicare Part B.\nClinical note: Patient has chronic left foot ulcer, 3x4 cm, Wagner grade 2 with necrotic tissue at wound base. Patient is diabetic with peripheral neuropathy. Debridement of devitalized subcutaneous tissue performed to promote healing. Wound has been present for 6 weeks with no improvement despite conservative wound care. Documentation supports medical necessity for active wound management.' },
  },

  // -----------------------------------------------------------------------
  // Coding Intelligence
  // -----------------------------------------------------------------------
  {
    id: 'ci-bundle-99213-11042',
    moduleId: 'coding-intelligence',
    title: '99213 + 11042: NCCI bundle?',
    desc: 'Check whether an office visit and debridement on the same day need a 25 modifier.',
    payload: { codes: '99213, 11042', icd: 'L97.521', modifiers: '', clinicalText: 'Professional claim for single provider, single patient, single date of service.\nLine 1: CPT 99213 — Office/outpatient visit, established patient, low complexity. Units: 1. Modifiers: None.\nLine 2: CPT 11042 — Debridement, subcutaneous tissue, first 20 sq cm. Units: 1. Modifiers: None.\nDiagnosis codes submitted: L97.521 — Non-pressure chronic ulcer of other part of left foot with fat layer exposed.\nBoth services performed same date of service by same provider. Verify NCCI edit pair status and modifier 25 requirement.' },
  },
  {
    id: 'ci-cataract-bilateral',
    moduleId: 'coding-intelligence',
    title: '66984 bilateral: 50 vs RT/LT',
    desc: 'Confirm modifier convention + MUE for bilateral cataract extraction.',
    payload: { codes: '66984', icd: 'H25.13', modifiers: '50', units: '2', clinicalText: 'Professional claim for single provider, single patient, single date of service.\nLine 1: CPT 66984 — Extracapsular cataract removal with insertion of intraocular lens prosthesis. Units: 2. Modifiers: 50 (bilateral).\nDiagnosis codes submitted: H25.13 — Age-related nuclear cataract, bilateral.\nBilling both eyes same session. Verify MUE cap for 66984, correct modifier convention (50 vs RT+LT), and whether bilateral billing is supported.' },
  },

  // -----------------------------------------------------------------------
  // Auth & Eligibility
  // -----------------------------------------------------------------------
  {
    id: 'ae-terminated',
    moduleId: 'auth-eligibility',
    title: 'Terminated coverage (270/271)',
    desc: 'Member ID with TERM marker -- the eligibility adapter returns inactive coverage.',
    payload: { memberId: 'UHC1234567TERM', firstName: 'JOHN', lastName: 'DOE', dateOfBirth: '1970-01-01', serviceTypeCode: '30', clinicalText: 'Payer Type: Medicare Part B\nMAC / Jurisdiction: Novitas\nDate of Service: 2026-06-01\nPlace of Service: 11 — Office\nCPT / HCPCS: 99214\nCPT Description: Office/outpatient visit, established patient, moderate complexity\nICD-10 Supporting Diagnoses: E11.9 — Type 2 diabetes mellitus without complications\nUnits: 1\nABN on File: No\nClinical Note: Patient presents for routine diabetes follow-up. Coverage verification needed -- member may have terminated plan.' },
  },
  {
    id: 'ae-active',
    moduleId: 'auth-eligibility',
    title: 'Active in-network member',
    desc: 'A clean 270/271 lookup showing active coverage and benefits.',
    payload: { memberId: 'UHC1234567', firstName: 'JOHN', lastName: 'DOE', dateOfBirth: '1970-01-01', serviceTypeCode: '30', clinicalText: 'Payer Type: Medicare Part B\nMAC / Jurisdiction: Novitas\nDate of Service: 2026-06-01\nPlace of Service: 11 — Office\nCPT / HCPCS: 99213\nCPT Description: Office/outpatient visit, established patient, low complexity\nICD-10 Supporting Diagnoses: J06.9 — Acute upper respiratory infection, unspecified\nUnits: 1\nABN on File: Not needed\nClinical Note: Established patient presents with 3-day history of sore throat and nasal congestion. Exam consistent with viral URI. Supportive care recommended.' },
  },
  // -----------------------------------------------------------------------
  // Payer Intelligence -- the analytics dashboard just refreshes
  // -----------------------------------------------------------------------
  {
    id: 'pi-summary',
    moduleId: 'payer-intelligence',
    title: 'Refresh payer + CARC summary',
    desc: 'Pulls denials-by-payer, CARC distribution, turnaround, and underpayments.',
    payload: { tab: 'analytics' },
  },
];

export function getSamplesForModule(moduleId) {
  return TEST_CASES.filter((c) => c.moduleId === moduleId);
}

export function getSampleById(id) {
  return TEST_CASES.find((c) => c.id === id) || null;
}

export default TEST_CASES;
